import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Translate from "../../utils/Translate";
import StUnitListItem from "./StUnitListItem";
import { useEffect, useRef, useState } from "react";
import { partType, canAssembleMultiPart } from "../../utils/part";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ActorLicensePreview from "../actor-license-print/ActorLicensePreview";
import ActorLicensePrintView from "../actor-license-print/ActorLicensePrintView";
import { useReactToPrint } from "react-to-print";
import Bowser from "bowser";
import YesOrNoDialog from "../YesOrNoDialog";
import PlaceholderStUnitListItem from "./PlaceholderStUnitListItem";
import { Add, Clear, Print } from "@mui/icons-material";
import AssembleMultiPartForm from "../parts/AssembleMultiPartForm";
import PrintAddressDialog from "../orders/PrintAddressDialog";

const cr80CardWidth = 2048;
const cr80cardHeight = 1300;
const browser = Bowser.getParser(window.navigator.userAgent);
const isSafari = browser.isBrowser("Safari");

function getStUnitSelectedByDefault(stUnits, isLicensePrint) {
  if (isLicensePrint) {
    // Not sure if this is important, but in internal ePort the stUnit for license
    // print was selected as the first one, sorted by descending fifoDate order
    stUnits = [...stUnits];
    stUnits.sort((a, b) => {
      if (!a.fifoDate) return 1; // a is null, move to end
      if (!b.fifoDate) return -1; // b is null, move to end
      return new Date(b.fifoDate) - new Date(a.fifoDate);
    });
  }

  return stUnits && stUnits.length > 0 ? stUnits[0].stUnitId : null;
}

function countAvailablePartsForMultiPartAssembly(orderLine) {
  const partChildren = orderLine.part.children ?? [];
  const availableQtyFromAssemblyPerChild = partChildren.map(({ childQty, child }) => {
    const availableChildStUnits = child.stUnits.filter(
      (s) => !s.bestBeforeDate || new Date(s.bestBeforeDate) > new Date(),
    );
    if (availableChildStUnits.some((s) => s.isUnlimitedQty)) {
      return Number.MAX_VALUE;
    }

    const availableChildQty = availableChildStUnits.map((s) => s.qty).reduce((acc, val) => acc + val, 0);
    return Math.floor(availableChildQty / childQty);
  });
  return Math.min(...availableQtyFromAssemblyPerChild);
}

// Sortera stUnits efter locationId, sen partId och sen fifoDate
function sortStUnits(stUnits) {
  return [...stUnits].sort((a, b) => {
    if (a.locationId !== b.locationId) {
      return (a.locationId ?? 0) - (b.locationId ?? 0);
    }

    if (a.partId !== b.partId) {
      return (a.partId ?? 0) - (b.partId ?? 0);
    }

    const aDate = a.fifoDate ? new Date(a.fifoDate) : new Date(0);
    const bDate = b.fifoDate ? new Date(b.fifoDate) : new Date(0);
    return aDate - bDate;
  });
}

export default function PickOrderLineDialog({
  orderLine,
  stUnits,
  printJob,
  onPickOrderLine,
  onClose,
  reloadData,
  deliveryAddress,
}) {
  if (
    (orderLine.part.partType === partType.plasticCardPrintJob ||
      orderLine.part.partType === partType.plasticCardPrintJobAndDigitalLicense) &&
    (orderLine.delQty > 0 || !printJob)
  ) {
    // console.log("PickOrderLineDialog invalid props", orderLine.part.partType, orderLine.delQty, printJob)
    throw new Error("PickOrderLineDialog invalid props");
  }

  const [isLicensePrint] = useState(
    orderLine.part.partType === partType.plasticCardPrintJob ||
      orderLine.part.partType === partType.plasticCardPrintJobAndDigitalLicense,
  );
  const [pickQty, setPickQty] = useState(null);

  stUnits = sortStUnits(stUnits);
  const [selectedStUnitId, setSelectedStUnitId] = useState(getStUnitSelectedByDefault(stUnits, isLicensePrint));
  const [selectedActorLicenseIds, setSelectedActorLicenseIds] = useState(
    printJob ? printJob.studentsPrintData.map((s) => s.actorLicenseId) : [],
  );
  const [canAssemble] = useState(canAssembleMultiPart(orderLine.part));
  const [showPrintDoneQuestion, setShowPrintDoneQuestion] = useState(false);
  const [showAssembleMultiPart, setShowAssembleMultiPart] = useState(false);
  const [showAddressPrintDialog, setShowAddressPrintDialog] = useState(false);

  // Update selected StUnit, since an StUnit may disappear if picked until empty
  useEffect(() => {
    if (selectedStUnitId && stUnits && stUnits.length > 0) {
      stUnits.findIndex((s) => s.stUnitId === selectedStUnitId);
      if (selectedStUnitId === -1) {
        setSelectedStUnitId(null);
      }
    }
  }, [selectedStUnitId, stUnits]);

  const printViewRef = useRef();
  const handlePrint = useReactToPrint({
    contentRef: printViewRef,
    // onAfterPrint: () => markAsPrinted(api, selectedActorLicenseIds),
    // I set the width and height of the page explicitly, and then do the same for the license to be printed.
    // This is because I couldn't get the license to size correctly by simply setting width and height 100%.
    // But it seems like it should be possible to make it completely dynamic using onBeforePrint and useRef.
    pageStyle: !isSafari
      ? `
      @page {
        margin: 0 !important;
        size: ${cr80CardWidth}px ${cr80cardHeight}px;
      }
    `
      : `
      @page {
        margin: 0 !important;
        size: ${cr80CardWidth}px ${cr80cardHeight}px;
      }

      @media print {
        html,
        body {
          transform: scale(0.54);
          translate: -160px -240px;
        }
      }
    `,
  });

  return (
    <>
      {showAssembleMultiPart && (
        <Dialog open className="partDialog" onClose={() => setShowAssembleMultiPart(false)}>
          <AssembleMultiPartForm
            defaultQty={orderLine.qty - orderLine.delQty}
            partId={orderLine.partId}
            onClose={() => {
              setShowAssembleMultiPart(false);
              reloadData();
            }}
            onSaved={async () => {
              await reloadData();
              setShowAssembleMultiPart(false);
            }}
          />
        </Dialog>
      )}
      {showAddressPrintDialog && (
        <PrintAddressDialog open onClose={() => setShowAddressPrintDialog(false)} address={deliveryAddress} />
      )}
      <Dialog
        open={!!orderLine}
        onClose={onClose}
        maxWidth={false}
        className={isLicensePrint ? "" : "mediumCourseDialog"}
        scroll="paper"
      >
        <DialogTitle sx={{ paddingBottom: "0px", paddingRight: "8px" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" paddingBottom={0} marginBottom={0}>
            <Box>{`${Translate.get("Pick")}: ${orderLine.part.description}`}</Box>
            <IconButton onClick={onClose}>
              <Clear />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {isLicensePrint && (
            <Stack direction="column" spacing={3} marginTop={2}>
              {printJob.studentsPrintData.map((s) => (
                <ActorLicensePreview
                  key={s.actorLicenseId}
                  printTemplate={printJob.printTemplate}
                  cardDetails={s}
                  isSelected={selectedActorLicenseIds.includes(s.actorLicenseId)}
                  onSelectedChanged={() =>
                    setSelectedActorLicenseIds((prev) =>
                      prev.includes(s.actorLicenseId)
                        ? prev.filter((id) => id !== s.actorLicenseId)
                        : [...prev, s.actorLicenseId],
                    )
                  }
                  showFront={true}
                  showBack={true}
                  printType="card"
                  showPrintSideTitleOverride={false}
                />
              ))}
            </Stack>
          )}

          {!isLicensePrint && stUnits && stUnits.length > 0 && (
            <>
              {orderLine.part.revision && (
                /* Based on discussion with Monica, she puts the latest revision in Part.Revision (via PartDialogForm.js) */
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                  }}
                >{`${Translate.get("LatestRevision")}: ${orderLine.part.revision ?? "-"}`}</Typography>
              )}
              <List>
                {stUnits.map((st) => (
                  <StUnitListItem
                    key={st.stUnitId}
                    {...st}
                    selected={st.stUnitId === selectedStUnitId}
                    onSelect={() => setSelectedStUnitId(st.stUnitId)}
                  />
                ))}
                {canAssemble && (
                  <PlaceholderStUnitListItem
                    icon={<Add />}
                    label={`${Translate.get("AssembleMultiPartLabel")} (${Translate.get(
                      "AvailableForMultiPartAssembly",
                    ).toLowerCase()}: ${countAvailablePartsForMultiPartAssembly(orderLine)})`}
                    onSelect={() => setShowAssembleMultiPart(true)}
                  />
                )}
              </List>
            </>
          )}

          {!isLicensePrint && canAssemble && (!stUnits || stUnits.length === 0) && (
            <List>
              <PlaceholderStUnitListItem
                icon={<Add />}
                label={`${Translate.get("AssembleMultiPartLabel")} (${Translate.get(
                  "AvailableForMultiPartAssembly",
                ).toLowerCase()}: ${countAvailablePartsForMultiPartAssembly(orderLine)})`}
                onSelect={() => setShowAssembleMultiPart(true)}
              />
            </List>
          )}

          {!isLicensePrint && !canAssemble && (!stUnits || stUnits.length === 0) && (
            <Stack width="100%" justifyContent="center" alignItems="center" marginTop="20px">
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                {Translate.get("NoStUnitAvailable")}
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Grid
            container
            justifyContent="flex-end"
            alignItems="flex-end"
            direction="column"
            spacing={1}
            marginTop="16px"
            marginLeft="16px"
            marginRight="16px"
            marginBottom="8px"
            width="100%"
          >
            {isLicensePrint && (
              <Grid container height="84px" justifyContent="stretch" alignItems="stretch" spacing={2} size={12}>
                <Grid size={6}>
                  <Box
                    onClick={() => (deliveryAddress ? setShowAddressPrintDialog(true) : null)}
                    className={"printAddressButton" + (!deliveryAddress ? " disabled" : "")}
                    height="100%"
                  >
                    <Grid container height="100%">
                      <Grid container size={11}>
                        {deliveryAddress ? (
                          <>
                            <Grid size={12}>{deliveryAddress?.street1}</Grid>
                            <Grid size={12}>{deliveryAddress?.street2}</Grid>
                            <Grid size={12}>
                              {deliveryAddress?.zipCode} {deliveryAddress?.city}
                            </Grid>
                          </>
                        ) : (
                          <Grid alignSelf="center" size={12}>
                            {Translate.get("NoDeliveryAddress")}
                          </Grid>
                        )}
                      </Grid>
                      <Grid alignSelf="center" size={1}>
                        <Print />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                <Grid size={6}>
                  <Stack direction="column" justifyContent="space-between" alignItems="stretch" height="100%">
                    <Button
                      variant="outlined"
                      fullWidth
                      disabled={!selectedActorLicenseIds || selectedActorLicenseIds?.length === 0}
                      onClick={handlePrint}
                    >
                      {`${Translate.get("Print")} (${selectedActorLicenseIds.length}/${
                        printJob.studentsPrintData.length
                      })`}
                    </Button>
                    <ButtonWithSpinner
                      fullWidth
                      disabled={
                        !selectedStUnitId ||
                        !stUnits.find((st) => st.stUnitId === selectedStUnitId) ||
                        stUnits.find((st) => st.stUnitId === selectedStUnitId).qty < pickQty
                      }
                      variant="contained"
                      onClick={() => setShowPrintDoneQuestion(true)}
                    >
                      {Translate.get("PrintDone")}
                    </ButtonWithSpinner>
                  </Stack>
                </Grid>
              </Grid>
            )}

            {!isLicensePrint && (
              <>
                <Grid container justifyContent="flex-end" alignItems="stretch" direction="row" spacing={1} size={12}>
                  <Grid size={3}>
                    <ButtonWithSpinner
                      sx={{ height: "40px" }}
                      variant="outlined"
                      fullWidth
                      disabled={pickQty > orderLine.qty - orderLine.delQty || !selectedStUnitId}
                      onClick={async () =>
                        pickQty > 0 ? await onPickOrderLine(orderLine.orderLineId, pickQty, selectedStUnitId) : null
                      }
                    >
                      {Translate.get("Pick")}:{" "}
                    </ButtonWithSpinner>
                  </Grid>
                  <Grid size={3}>
                    <TextField
                      value={pickQty ?? ""}
                      disabled={!stUnits || stUnits.length === 0}
                      onChange={(ev) => setPickQty(ev.target.value)}
                      placeholder={Translate.get("Quantity")}
                      fullWidth
                      size="small"
                      inputProps={{
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                        type: "number",
                        min: 1,
                        max: orderLine.qty - orderLine.delQty,
                      }}
                      error={
                        pickQty > orderLine.qty - orderLine.delQty ||
                        (selectedStUnitId &&
                          stUnits.find((st) => st.stUnitId === selectedStUnitId) &&
                          stUnits.find((st) => st.stUnitId === selectedStUnitId).qty < pickQty)
                      }
                      helperText={
                        pickQty > orderLine.qty - orderLine.delQty
                          ? `${Translate.get("Max")} ${orderLine.qty - orderLine.delQty}`
                          : selectedStUnitId &&
                              stUnits.find((st) => st.stUnitId === selectedStUnitId) &&
                              stUnits.find((st) => st.stUnitId === selectedStUnitId).qty < pickQty
                            ? Translate.get("StUnitQtyNotEnough")
                            : ""
                      }
                    />
                  </Grid>
                </Grid>
                <Grid container justifyContent="space-between" alignItems="flex-end" direction="row" size={12}>
                  <Grid>{/* Empty on purpose */}</Grid>
                  <Grid container justifyContent="center" size={6}>
                    <Grid>{Translate.get("Or").toLowerCase()}</Grid>
                  </Grid>
                </Grid>
                <Grid container justifyContent="flex-end" alignItems="stretch" direction="row" spacing={1} size={12}>
                  <Grid size={6}>
                    <ButtonWithSpinner
                      fullWidth
                      disabled={
                        !selectedStUnitId ||
                        !stUnits.find((st) => st.stUnitId === selectedStUnitId) ||
                        stUnits.find((st) => st.stUnitId === selectedStUnitId).qty < pickQty
                      }
                      variant="contained"
                      onClick={async () =>
                        await onPickOrderLine(orderLine.orderLineId, orderLine.qty - orderLine.delQty, selectedStUnitId)
                      }
                    >{`${Translate.get("Pick")} ${Translate.get("All")} (${
                      orderLine.qty - orderLine.delQty
                    })`}</ButtonWithSpinner>
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        </DialogActions>
      </Dialog>
      {/* ActorLicensePrintView is only visible when printing, via media query in ActorLicensePrint.css */}
      {isLicensePrint && (
        <ActorLicensePrintView
          ref={printViewRef}
          printTemplate={printJob.printTemplate}
          studentCardsDetails={printJob.studentsPrintData.filter((s) =>
            selectedActorLicenseIds.includes(s.actorLicenseId),
          )}
          printFront={true}
          printBack={true}
          printWidth={cr80CardWidth}
          printHeight={cr80cardHeight}
          printType="card"
        />
      )}
      <YesOrNoDialog
        open={showPrintDoneQuestion}
        text={Translate.get("SurePrintDone")}
        onYes={async () => await onPickOrderLine(orderLine.orderLineId, orderLine.qty, selectedStUnitId)}
        onNo={() => setShowPrintDoneQuestion(false)}
      />
    </>
  );
}
